import { playjectorHostModeState, hostState, matchmakerState, machineIdState, machineNameState, adModeState, hostCampaignConfigState, hostMatchmakingState, authState } from '../../stores/shared';
import { sendMessage } from '../realtime';
import { websocket, MatchmakingMessageType, generators } from '@playcastdotio/Messaging';
import { ipcSend } from '../../messaging/protocols/ipc';
import { HostBroadcastStatus } from '@playcastdotio/CoreTypes';
import { getPersistentPlayerId } from '../../utilities/persistentId';
import { sendAnalytics, PlaycastAnalyticsGameLaunched, PlaycastAnalyticsGameClosed } from '@playcastdotio/Analytics';
import * as Sentry from '@sentry/react';
import constants from '@playcastdotio/GlobalConfig';
import { sendMessage as hostRealtimeSend } from '../realtime';
import { sendMatchmakerHostConfig } from '../managers/matchmaker';
import { getIpcFunctions, setWindowState } from '../ipcInterface';
import config from '../../../config.json';
import { campaignSwitchTracker } from '../managers/campaignSwitchTracker';
import { attachSentryTraceToMessage } from '../realtime/tracing';
import { USE_MESSAGE_ROUTER } from '../../../config/featureFlags';

let logoutTimer: ReturnType<typeof setTimeout> | null = null;
let lastGameName: string | null = null;
let lastGameId: number | null = null;
let lastGameLaunched = false;

/**
 * Monitors the login state when in pavilion mode and streaming.
 * If the user has been logged out for 5 minutes, it will automatically stop the stream.
 * Also handles updating the matchmaking invitation when Steam game information changes.
 *
 * LEGACY: Disabled when USE_MESSAGE_ROUTER is true (GameLaunchCoordinator handles this)
 */
export const initializePlayjectorHostModeEvents = () => {
  // TODO-STRANGLER-FIG: Remove when GameLaunchCoordinator fully owns game lifecycle
  if (USE_MESSAGE_ROUTER) {
    console.log('[PlayjectorHostMode] Skipping legacy event listener (GameLaunchCoordinator is active)');
    return;
  }

  // Monitor changes to the playjector host mode state
  playjectorHostModeState.addListener((playjectorState) => {
    const isStreaming = hostState.currentState.status === 'streaming';
    const isPavilionMode = hostState.currentState.edition === 'pavilion';

    // Track gameLaunched state changes and send to Sentry
    if (playjectorState.gameLaunched !== lastGameLaunched) {
      const machineId = machineIdState.currentState || 'unknown';
      const machineName = machineNameState.currentState || 'unknown';

      if (playjectorState.gameLaunched) {
        // Game launched: true
        console.log('[PlayjectorHostMode] 🎮 Game Launched:', {
          gameName: playjectorState.gameName,
          gameId: playjectorState.gameId,
          machineId,
        });

        // Log as breadcrumb - normal operation, useful context for debugging
        Sentry.addBreadcrumb({
          category: 'game_lifecycle',
          message: `Game Launched: ${playjectorState.gameName || 'unknown'}`,
          level: 'info',
          data: {
            event_type: 'game_launched',
            game_id: playjectorState.gameId.toString(),
            game_name: playjectorState.gameName || 'unknown',
            machine_id: machineId,
            machine_name: machineName,
            edition: hostState.currentState.edition || 'unknown',
            streaming: isStreaming.toString(),
            steam_launched: playjectorState.steamLaunched,
            user_logged_in: playjectorState.userLoggedIn,
            is_pavilion_mode: isPavilionMode,
            lobby_id: matchmakerState.currentState.stateDetails?.currentMatchInvitationId || '',
            version: constants.VERSION,
          },
        });
      } else {
        // Game launched: false
        console.log('[PlayjectorHostMode] 🛑 Game Closed/Stopped:', {
          previousGameName: lastGameName,
          previousGameId: lastGameId,
          machineId,
        });

        // Log as breadcrumb - normal operation, useful context for debugging
        Sentry.addBreadcrumb({
          category: 'game_lifecycle',
          message: `Game Stopped: ${lastGameName || 'unknown'}`,
          level: 'info',
          data: {
            event_type: 'game_stopped',
            game_id: lastGameId?.toString() || 'unknown',
            game_name: lastGameName || 'unknown',
            machine_id: machineId,
            machine_name: machineName,
            edition: hostState.currentState.edition || 'unknown',
            streaming: isStreaming.toString(),
            user_logged_in: playjectorState.userLoggedIn,
            is_pavilion_mode: isPavilionMode,
            lobby_id: matchmakerState.currentState.stateDetails?.currentMatchInvitationId || '',
            version: constants.VERSION,
          },
        });

        if (
          hostState.currentState.edition === 'pavilion' &&
          adModeState.currentState.enabled
        ) {
          if (hostState.currentState.status === 'streaming' || hostState.currentState.status === 'starting') {
            // Stopping the stream will make the players leave causing a restart.
            const ipcFuncs = getIpcFunctions();
            ipcFuncs.stopStream('ad_mode:game_closed');
          } else if (hostMatchmakingState.currentState.gameState !== 'prewarming') {
            // Game closed outside of streaming session AND not during a restart
            // Update host state to reflect that game is no longer running
            // Don't touch 'prewarming' state - that's managed by the restart flow
            console.log('[PlayjectorHostMode] Game closed outside streaming/restart session, updating state to idle');
            hostMatchmakingState.updateState({
              ...hostMatchmakingState.currentState,
              gameState: 'idle',
              // Keep campaignId - capacity manager will decide whether to reassign or switch
              // matchmakeable is already handled by state machine (gameLaunched: false)
            });
          } else {
            console.log('[PlayjectorHostMode] Game closed during prewarming (expected), keeping prewarming state');
          }
        }
      }

      lastGameLaunched = playjectorState.gameLaunched;
    }

    // Handle Steam game information updates
    if (isStreaming && isPavilionMode && matchmakerState.currentState.stateDetails?.currentMatchInvitationId) {
      const gameNameChanged = playjectorState.gameName !== lastGameName;
      const gameIdChanged = playjectorState.gameId !== lastGameId;

      if (gameNameChanged || gameIdChanged) {
        console.log('[PlayjectorHostMode] Steam game information changed:', {
          gameName: playjectorState.gameName,
          gameId: playjectorState.gameId,
        });

        // Game Booted
        if (playjectorState.gameName !== '') {
          sendAnalytics({
            name: 'Game Launched',
            role: 'host',
            gameId: playjectorState.gameId,
            gameName: playjectorState.gameName,
            source: 'electron',
          } as PlaycastAnalyticsGameLaunched);
        } else if (playjectorState.gameName === '' && lastGameName !== null) {
          // Only send this event if a game was previously launched
          sendAnalytics({
            name: 'Game Closed',
            role: 'host',
            gameId: playjectorState.gameId,
            gameName: playjectorState.gameName,
            source: 'electron',
          } as PlaycastAnalyticsGameClosed);
        }

        // Update the invitation with new Steam game information
        sendMessage(
          websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_UPDATE_INVITATION, {
            invitationId: matchmakerState.currentState.stateDetails.currentMatchInvitationId,
            name: playjectorState.gameName,
            steamId: playjectorState.gameId.toString(),
          }),
        );

        // Update last known values
        lastGameName = playjectorState.gameName;
        lastGameId = playjectorState.gameId;
      }
    }

    // Check if we're streaming in pavilion mode
    if (isStreaming && isPavilionMode) {
      // If user is logged in, clear any existing timer
      if (playjectorState.userLoggedIn) {
        if (logoutTimer) {
          clearTimeout(logoutTimer);
          logoutTimer = null;
          console.log('[PlayjectorHostMode] User logged in, cleared logout timer');
        }
      }
      // If user is logged out and no timer is set, start a timer
      else if (!playjectorState.userLoggedIn && !logoutTimer) {
        console.log('[PlayjectorHostMode] User logged out, starting 10 minute timer before stopping stream');
        // logoutTimer = setTimeout(() => {
        //   console.log('[PlayjectorHostMode] User has been logged out for 10 minutes, stopping stream');
        //   stopStream('playjector:host_mode:steam_user_logged_out');
        //   logoutTimer = null;
        // }, config.PAVILION_STEAM_LOGOUT_TIMEOUT);
      }
    }
    // If we're not streaming or not in pavilion mode, clear any existing timer
    else if (logoutTimer) {
      clearTimeout(logoutTimer);
      logoutTimer = null;
      console.log('[PlayjectorHostMode] No longer streaming in pavilion mode, cleared logout timer');
    }
  });

  let cachedStatus: HostBroadcastStatus;

  // Also monitor the host state for changes in streaming status or edition
  hostState.addListener((state) => {
    const isStreaming = state.status === 'streaming';
    const isPavilionMode = state.edition === 'pavilion';
    const isUserLoggedOut = !playjectorHostModeState.currentState.userLoggedIn;

    // If we start streaming in pavilion mode while logged out, start timer
    if (isStreaming && isPavilionMode && isUserLoggedOut && !logoutTimer) {
      console.log('[PlayjectorHostMode] Started streaming in pavilion mode while logged out, starting 2 minute timer');
      // logoutTimer = setTimeout(() => {
      //   console.log('[PlayjectorHostMode] User has been logged out for 2 minutes, stopping stream');
      //   stopStream('playjector:host_mode:steam_user_logged_out');
      //   logoutTimer = null;
      // }, config.PAVILION_STEAM_LOGOUT_TIMEOUT);
    }
    // If we stop streaming or change out of pavilion mode, clear timer
    else if ((!isStreaming || !isPavilionMode) && logoutTimer) {
      clearTimeout(logoutTimer);
      logoutTimer = null;
      console.log('[PlayjectorHostMode] No longer streaming in pavilion mode, cleared logout timer');
    }

    if (state.status !== cachedStatus) {
      console.log('[Playjector Broadcast Status] Host state status changed.');
      ipcSend(generators.hub.broadcastStatus(state.status));
    }
    cachedStatus = state.status;
  });
};
